import { useEffect, useRef, useState, useCallback } from 'react';
import type { WSMessage } from '../types/game';
import { getUserId } from '../utils';
import { getWsBaseUrl } from '../discord/discordSdk';

interface UseWebSocketOptions {
  roomId: string;
  clientId?: string;
  enabled?: boolean;
  onMessage?: (message: WSMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  sendMessage: (message: WSMessage) => void;
  disconnect: () => void;
  reconnect: () => void;
}

export function useWebSocket({
  roomId,
  clientId,
  enabled = true,
  onMessage,
  onOpen,
  onClose,
  onError,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const connectionIdRef = useRef(0); // Уникальный ID для каждой попытки подключения

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 2000;

  const getWebSocketUrl = useCallback(() => {
    const baseUrl = getWsBaseUrl();
    const userId = clientId || getUserId();
    return `${baseUrl}/room/${roomId}?clientId=${encodeURIComponent(userId)}`;
  }, [roomId, clientId]);

  const sendMessage = useCallback((message: WSMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
      setError('Not connected to server');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      // Удаляем обработчики перед закрытием чтобы не было лишних событий
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS;

    if (isMountedRef.current) {
      setIsConnected(false);
      setIsConnecting(false);
    }
  }, []);

  const connect = useCallback(() => {
    // Проверяем что компонент всё ещё смонтирован
    if (!isMountedRef.current) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Закрываем предыдущее соединение если есть
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    // Увеличиваем ID подключения - это позволит игнорировать события от старых соединений
    connectionIdRef.current += 1;
    const currentConnectionId = connectionIdRef.current;

    setIsConnecting(true);
    setError(null);

    try {
      const url = getWebSocketUrl();
      console.log(`Connecting to WebSocket: ${url}`);

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        // Проверяем что это актуальное соединение и компонент смонтирован
        if (!isMountedRef.current || currentConnectionId !== connectionIdRef.current) {
          ws.close();
          return;
        }

        console.log('WebSocket connected');
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttemptsRef.current = 0;
        onOpen?.();
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current || currentConnectionId !== connectionIdRef.current) return;

        try {
          const message = JSON.parse(event.data) as WSMessage;
          console.log('WebSocket message received:', message);
          onMessage?.(message);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        if (!isMountedRef.current || currentConnectionId !== connectionIdRef.current) return;

        console.error('WebSocket error:', event);
        setError('Connection error');
        onError?.(event);
      };

      ws.onclose = () => {
        if (!isMountedRef.current || currentConnectionId !== connectionIdRef.current) return;

        console.log('WebSocket disconnected');
        setIsConnected(false);
        setIsConnecting(false);
        wsRef.current = null;
        onClose?.();

        // Автоматическое переподключение
        if (isMountedRef.current && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `Reconnecting... (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connect();
            }
          }, RECONNECT_DELAY);
        } else if (isMountedRef.current) {
          setError('Connection lost. Please reconnect manually.');
        }
      };
    } catch (err) {
      if (!isMountedRef.current) return;

      console.error('Failed to create WebSocket:', err);
      setError('Failed to connect');
      setIsConnecting(false);
    }
  }, [getWebSocketUrl, onMessage, onOpen, onClose, onError]);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connectionIdRef.current += 1;
    setTimeout(() => {
      if (isMountedRef.current) {
        connect();
      }
    }, 100);
  }, [connect, disconnect]);

  // Эффект для отслеживания mounted состояния
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Эффект для автоматического подключения
  useEffect(() => {
    if (enabled && roomId && isMountedRef.current) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [roomId, enabled]);

  return {
    isConnected,
    isConnecting,
    error,
    sendMessage,
    disconnect,
    reconnect,
  };
}
